/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { useZoneCellsData } from "./hooks";
import { prepareZoneCellDataGrid } from "../helpers";
import { useStoreDispatch } from "../../../store/hooks";
import "../Config.css";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { useSelector } from "react-redux";
import { basicDialogSelector, confirmDialogSelector, isLoadingStatusSelector } from "../../../store/helpers/helperSelectors";
import AddZoneModal from "../../../components/Configuration/Zone/AddZone";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { IFullDeviceDetails, IZoneDevice } from "../../../store/api/deviceConfig/types";
import { setBasicDialogOpenFor, setConfirmDialog } from "../../../store/helpers/helperReducers";
import { setIsAddNewZoneDevice, setIsZoneAdded } from "../../../store/api/deviceConfig/reducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../globals/constants";
import { isZoneAddedSelector } from "../../../store/api/deviceConfig/selectors";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
// remove below mockdata once get it from backend----------

const NrCells: React.FC = () => {
  const dispatch = useStoreDispatch();
  const {
    permissions,
    data,
    isLoading,
    zonesColumns,
    NoOfSelectedRow,
    setNoOfSelectedRow,
    selectedRow,
    setSelectedRow,
    selectedRowIDs,
    setSelectedRowIDs,
    handleEnableBtn,
    handleDeleteZone,
    deviceDetail,
    selectedZoneData,
    refetch,
    isFetching
  } = useZoneCellsData();

  const getDataFromApi = data?.data || [];
  const rowsData = prepareZoneCellDataGrid(getDataFromApi);
  const noOfRows = data?.totalItems || 0;
  const [modelOpenMode, setModelOpenMode] = React.useState("Add");
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const isOfflineDevice = selectedDevice?.configurationStatus?.toLowerCase() === "offline";

  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const isLoadingAddZone = useSelector(isLoadingStatusSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const isZoneAdded = useSelector(isZoneAddedSelector);

  const handleOnAddZoneCell = () => {
    handleClearSelection();
    setModelOpenMode("Add");
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ZONE));
    dispatch(setIsAddNewZoneDevice(true));
  };


  React.useEffect(() => {
    if (!isFetching && isZoneAdded) {
      dispatch(setIsZoneAdded(false));
    }
  }, [isFetching]);

  const handleOnEdit = () => {
    setModelOpenMode("Edit");
    dispatch(setIsAddNewZoneDevice(false));
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.ZONE));
  };

  const handleClearSelection = () => {
    setSelectedRowIDs([]);
    setNoOfSelectedRow(0);
    setSelectedRow([]);
  };

  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {
    setSelectedRowIDs(rowIds);
    const rowIdLength = rowIds?.length;
    setNoOfSelectedRow(rowIdLength);
    const selectedRowsData: IZoneDevice[] = rowsData?.filter((el) => el?.id === rowIds[0]);
    if (rowIdLength === 1 && selectedRowsData?.length > 0) {
      setSelectedRow(selectedRowsData);
    } else setSelectedRow([]);
  };
  const text911 = selectedRow[0]?.barringforemergency === "True" ? "911 DISABLE" : "911 ENABLE";
  const modalStr = selectedRow[0]?.barringforemergency === "True" ? "DISABLE 911" : "ENABLE 911";

  return (
    <>
      {selectedRow?.length === 1 && openConfirmDialog === DIALOGOPENFOR.ZONEDELETE && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteZone}>
          Are you sure you want to delete <strong>{`Zone "${selectedRow[0]?.index}" `}</strong>?
        </ConfirmDialog>
      )}
      {openConfirmDialog === DIALOGOPENFOR.ENABLE911 && (
        <ConfirmDialog
          actionName={text911?.toLowerCase().includes("enable") ? constStr.enable : constStr.disable}
          handleClickAction={handleEnableBtn}>
          Are you sure you want to {`${modalStr}`}?
        </ConfirmDialog>
      )}
      {basicDialogOpenFor === DIALOGOPENFOR.ZONE && (
        <AddZoneModal
          modelOpenMode={modelOpenMode}
          selectedRow={selectedRow?.length > 0 ? selectedRow[0] : selectedZoneData}
          handleClear={handleClearSelection}
        />
      )}

      <div
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "18px",
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--card)",
        }}
      >
        {isLoading && <p>Loading...</p>}
        {!isLoading && (
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                {NoOfSelectedRow > 0 ? (
                  <div className="flex items-center gap-2">
                    <p className="text-sm">{`${NoOfSelectedRow} selected`}</p>
                    {permissions?.hasOpCodeGroup("manage_sn") && deviceDetail.nodeType === "SN" && NoOfSelectedRow === 1 && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => dispatch(setConfirmDialog(DIALOGOPENFOR.ENABLE911))}
                        disabled={isLoadingAddZone["deleteZone"] || isLoadingAddZone["enable911"]}
                      >
                        {isLoadingAddZone["enable911"] && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {text911}
                      </Button>
                    )}
                    {permissions?.hasOpCodeGroup("manage_topology") && NoOfSelectedRow === 1 && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={isLoadingAddZone["deleteZone"] || isLoadingAddZone["enable911"]}
                          onClick={handleOnEdit}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => dispatch(setConfirmDialog(DIALOGOPENFOR.ZONEDELETE))}
                          disabled={isLoadingAddZone["deleteZone"] || isLoadingAddZone["enable911"]}
                        >
                          {isLoadingAddZone["deleteZone"] && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          <Icon name="delete" size={16} />
                          Delete
                        </Button>
                      </>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isLoadingAddZone["deleteZone"] || isLoadingAddZone["enable911"]}
                      onClick={handleClearSelection}
                    >
                      Clear
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm">{`Showing ${noOfRows} results`}</p>
                )}
              </div>
              {permissions?.hasOpCodeGroup("manage_topology") && !isOfflineDevice && (
                <Button
                  className="h-10 px-4 text-sm font-medium border border-input bg-background shadow-sm flex items-center gap-2 hover:bg-muted hover:text-accent-foreground transition-colors"
                  variant="outline"
                  onClick={handleOnAddZoneCell}
                  disabled={isZoneAdded || (isLoadingAddZone["addZone"] || isLoadingAddZone["deleteZone"] || isLoadingAddZone["enable911"])}
                >
                  {isLoadingAddZone["addZone"] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon name="add" size={18} />}
                  Add Zone
                </Button>
              )}
            </div>
            <CustomDataGrid
              tableId="zone"
              rows={rowsData}
              columns={zonesColumns}
              checkboxSelection={true}
              rowCount={noOfRows}
              loading={isLoading}
              onRowSelectionModelChange={handleOnRowSelected}
              rowSelectionModel={selectedRowIDs}
              disableRowSelectionOnClick={true}
              getRowId={(row: IZoneDevice) => row.id}
              isRowSelectable={() => !isOfflineDevice}
              sx={{
                "& .MuiDataGrid-virtualScroller": { overflowY: "auto" },
                "& .MuiDataGrid-scrollbar--vertical": { display: "block" },
              }}
              fillContainer={false}
            />
          </div>
        )}
      </div>

    </>
  );
};

export default NrCells;
